import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { DollarSign, Percent, Plus, Trash2, Upload, FileText, Calendar, Route, Package, Settings, ArrowRight } from "lucide-react";
import AddressAutocomplete from "@/components/ui/AddressAutocomplete";
import StopAddressField from "@/components/ui/StopAddressField";
import { DatePicker } from "@/components/ui/date-picker";
import ItemMultiSelect, { emptyItemRow, deriveItemFields } from "@/components/ui/ItemMultiSelect";
import TollMultiSelect, { tollTotal } from "@/components/ui/TollMultiSelect";

// Document categories that can be attached to a job at creation. Staged locally
// and uploaded to the job's e_doc_files right after it's created. Keep in sync
// with DOC_TYPES in ClientJobs.jsx.
const DOC_TYPES = ["Delivery Order", "Outturn Report", "Delivery Docket", "Interim Receipt", "Booking Confirmation", "Other"];

const Field = ({ label, children, required }) => (
  <div className="space-y-1.5">
    <label className="block font-semibold text-white/85 text-sm">
      {label}{required ? "*" : ""}
    </label>
    {children}
  </div>
);

// Titled, card-wrapped section — groups related fields so the long Create Job
// form reads as clear chunks instead of one flat list.
const Section = ({ title, icon: Icon, accent = "#19e6c8", desc, children }) => (
  <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 sm:p-5">
    <div className="flex items-center gap-2.5 mb-4">
      {Icon && (
        <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accent}1f`, color: accent }}>
          <Icon size={16} />
        </span>
      )}
      <div className="min-w-0">
        <h3 className="brand-title text-sm md:text-base leading-tight">{title}</h3>
        {desc && <p className="text-[11px] text-white/45 mt-0.5">{desc}</p>}
      </div>
    </div>
    {children}
  </section>
);

const YN = ["Yes", "No", "N/A"];

// Freight type options. "Other (Specify)" reveals a free-text field.
const FREIGHT_TYPES = ["LCL", "Airfreight", "General", "FCL", "Interstate"];
const FREIGHT_OTHER = "Other (Specify)";

// Shipment shape — "single" (one sender, one receiver) covers the vast
// majority of bookings and is always the default; the multi_* options are
// rare and reveal the pickup/delivery stops editor below when chosen.
const SHIPMENT_MODES = [
  { value: "single",       label: "Single pickup & single delivery" },
  { value: "multi_pickup", label: "Multiple pickups → one delivery" },
  { value: "multi_dropoff", label: "One pickup → multiple deliveries" },
  { value: "multi_both",   label: "Multiple pickups & multiple deliveries" },
];

// Accessorial service charges billed to the customer (sell side, AUD) when the
// option is "Yes". Keep in sync with ACCESSORIAL_CHARGES in backend/pdfgen.py.
const ACCESSORIAL_CHARGES = [
  ["inbound_connect", "Inbound Connect", 35],
  ["tail_lift",       "Tail Lift",       100],
  ["hand_unload",     "Hand Unload",     55],
  ["dangerous_goods", "Dangerous Goods", 55],
];

const EMPTY_ADDR = {
  label: "", line1: "", line2: "", suburb: "", state: "", postcode: "",
  contact_name: "", contact_phone: "", notes: "", operating_hours: "",
};

// Service type — express is purely a same-day distinction; it bills exactly
// like Standard (Sell Rate is entered manually by the admin either way).
// Keep in sync with JobStatus.jsx.
const SERVICE_TYPES = [
  { value: "standard", label: "Standard" },
  { value: "express",  label: "Express (same-day)" },
];

const todayIso = () => new Date().toISOString().slice(0, 10);
const tomorrowIso = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};
const defaultDateFor = (serviceType) => (serviceType === "express" ? todayIso() : tomorrowIso());

export default function CreateJob({ onCreated, clientMode = false }) {
  const { user } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [savedAddresses, setSavedAddresses] = useState([]); // address book, for Business Name search
  const [submitting, setSubmitting] = useState(false);
  // Documents staged for upload — sent to the job's e-docs right after creation.
  const [pendingDocs, setPendingDocs] = useState([]);
  const [docType, setDocType] = useState(DOC_TYPES[0]);

  // Pickup / drop-off are now entered inline as full address-book records and
  // saved to the address book on submit.
  const [pickup, setPickup] = useState({ ...EMPTY_ADDR });
  const [dropoff, setDropoff] = useState({ ...EMPTY_ADDR });
  const [legs, setLegs] = useState([]); // intermediate stops

  const [tollSelected, setTollSelected] = useState([]); // multi-select toll preset keys
  const [tollOther, setTollOther] = useState(""); // custom toll amount ("" = not selected)
  const [freightSel, setFreightSel] = useState("LCL"); // dropdown selection (may be "Other")
  const [itemRows, setItemRows] = useState(() => [emptyItemRow()]); // multi-select item type + qty rows
  // Tracks whether Job Date is still following the Service Type default
  // (tomorrow for standard, today for express) — turns off the moment the
  // user picks a date themselves, so we never clobber a manual choice.
  const [jobDateAuto, setJobDateAuto] = useState(true);

  const [form, setForm] = useState({
    job_date: defaultDateFor("standard"),
    job_ref: "",
    inbound_connect: "No",
    tail_lift: "No",
    hand_unload: "No",
    dangerous_goods: "No",
    service_type: "standard",
    freight_type: "LCL",
    volume_cbm: 0,
    weight_kgs: 0,
    dimensions: "",
    notes: "",
    e_docs: [],
    assigned_driver_id: "",
    client_user_id: "",
    buy_rate: "",
    base_rate: "",
    fuel_levy_pct: "",
    toll: "",
    extra_line_items: [],
    shipment_mode: "single",
  });

  const handleServiceType = (val) => {
    set("service_type", val);
    // Only follow the new default if the date hasn't been hand-picked yet.
    if (jobDateAuto) set("job_date", defaultDateFor(val));
  };

  const handleJobDate = (val) => {
    setJobDateAuto(false);
    set("job_date", val);
  };

  const handleFreightSel = (val) => {
    setFreightSel(val);
    // For a preset the value is the label itself; for "Other" we clear it so
    // the user types their own, which becomes the stored freight_type.
    set("freight_type", val === FREIGHT_OTHER ? "" : val);
  };

  // Toll total is the sum of every selected preset + the custom "other"
  // amount — keeps `form.toll` (used by the live pricing summary + payload)
  // in sync whenever the multi-select checkboxes change.
  useEffect(() => {
    setForm((f) => ({ ...f, toll: tollTotal(tollSelected, tollOther) }));
  }, [tollSelected, tollOther]);

  const isClient = clientMode || user?.role === "client";
  // Allocator is a restricted staff role — creates jobs like admin, but must
  // never see or set pricing/billing (rate card, accessorial $, GST total).
  const isAllocator = user?.role === "allocator";
  const hidePricing = isClient || isAllocator;
  const isMultiStop = form.shipment_mode !== "single";
  // New stops default to the type the declared shipment shape implies, rather
  // than always "intermediate" — admin/customer can still change it per stop.
  const defaultStopType = () => {
    if (form.shipment_mode === "multi_pickup") return "pickup";
    if (form.shipment_mode === "multi_dropoff") return "dropoff";
    if (form.shipment_mode === "multi_both") return "pickup";
    return "intermediate";
  };

  const loadAll = async () => {
    try {
      // Saved address book powers the Business Name search (admin sees all,
      // clients see their own — handled server-side).
      const a = await api.get("/addresses");
      setSavedAddresses(a.data);
      if (!isClient) {
        const [d, c] = await Promise.all([api.get("/drivers"), api.get("/customers")]);
        setDrivers(d.data);
        setCustomers(c.data);
      }
    } catch (e) { console.error(e); }
  };
  useEffect(() => { loadAll(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Custom/extra invoice line items (e.g. "Shrink wrapping charges -
  // Interport") added at booking time — kept on the job so they auto-map onto
  // the invoice (see _job_line_items() in the backend).
  const addExtraLineItem = () => {
    setForm((f) => ({
      ...f,
      // amount starts as "" (not 0) so the field is free to type into — matches
      // the empty-string-default convention the other pricing inputs use above.
      extra_line_items: [...(f.extra_line_items || []), { id: crypto.randomUUID?.() || String(Math.random()), description: "", amount: "" }],
    }));
  };
  const updateExtraLineItem = (idx, field, val) => {
    setForm((f) => ({
      ...f,
      extra_line_items: (f.extra_line_items || []).map((it, i) => (i === idx ? { ...it, [field]: val } : it)),
    }));
  };
  const removeExtraLineItem = (idx) => {
    setForm((f) => ({
      ...f,
      extra_line_items: (f.extra_line_items || []).filter((_, i) => i !== idx),
    }));
  };

  const formatAddr = (a) => `${a.line1}${a.line2 ? ", " + a.line2 : ""}${a.suburb ? ", " + a.suburb : ""}${a.state ? " " + a.state : ""}${a.postcode ? " " + a.postcode : ""}`;
  const addrString = (a) => (a.label?.trim() ? `${a.label.trim()} — ${formatAddr(a)}` : formatAddr(a));

  // Save the inline pickup/drop-off entry to the address book. Non-fatal: a
  // failure here must not block job creation.
  const saveToAddressBook = async (a, type) => {
    if (!a.line1.trim()) return;
    try {
      await api.post("/addresses", {
        label: a.label?.trim() || a.line1.trim(),
        line1: a.line1, line2: a.line2, suburb: a.suburb, state: a.state, postcode: a.postcode,
        contact_name: a.contact_name, contact_phone: a.contact_phone,
        type, notes: a.notes, operating_hours: a.operating_hours,
      });
    } catch (e) { /* ignore — address book save is best-effort */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!pickup.line1.trim() || !dropoff.line1.trim()) {
      toast.error("Pick-up and drop-off addresses are required");
      return;
    }
    setSubmitting(true);
    try {
      // Persist both addresses to the address book (admin & client).
      await Promise.all([
        saveToAddressBook(pickup, "pickup"),
        saveToAddressBook(dropoff, "dropoff"),
      ]);

      const payload = {
        ...form,
        pickup_address: addrString(pickup),
        dropoff_address: addrString(dropoff),
        pickup_contact_name: pickup.contact_name,
        pickup_contact_phone: pickup.contact_phone,
        dropoff_contact_name: dropoff.contact_name,
        dropoff_contact_phone: dropoff.contact_phone,
        pickup_operating_hours: pickup.operating_hours,
        dropoff_operating_hours: dropoff.operating_hours,
        pickup_address_notes: pickup.notes,
        dropoff_address_notes: dropoff.notes,
        ...deriveItemFields(itemRows),
        volume_cbm: Number(form.volume_cbm),
        weight_kgs: Number(form.weight_kgs),
        buy_rate: Number(form.buy_rate),
        base_rate: Number(form.base_rate),
        fuel_levy_pct: Number(form.fuel_levy_pct),
        toll: Number(form.toll),
        assigned_driver_id: form.assigned_driver_id || null,
        client_user_id: form.client_user_id || null,
        legs: legs.filter((l) => l.address.trim()),
        extra_line_items: (form.extra_line_items || [])
          .filter((it) => (it.description || "").trim())
          .map((it) => ({ id: it.id, description: it.description.trim(), amount: Number(it.amount) || 0 })),
      };
      const { data: created } = await api.post("/jobs", payload);
      // Upload any attached documents to the freshly-created job (best-effort —
      // a doc failure shouldn't undo the booking).
      if (created?.id && pendingDocs.length > 0) {
        try {
          for (const pd of pendingDocs) {
            const fd = new FormData();
            fd.append("files", pd.file);
            fd.append("doc_type", pd.doc_type);
            await api.post(`/jobs/${created.id}/e-docs`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          }
        } catch (docErr) {
          toast.error("Job created, but some documents failed to upload — add them from the job card.");
        }
      }
      toast.success(isClient ? "Request submitted for admin review" : "Job created");
      setForm((f) => ({ ...f, job_ref: "", shipment_mode: "single", service_type: "standard", job_date: defaultDateFor("standard") }));
      setJobDateAuto(true);
      setPickup({ ...EMPTY_ADDR });
      setDropoff({ ...EMPTY_ADDR });
      setLegs([]);
      setPendingDocs([]);
      setItemRows([emptyItemRow()]);
      setTollSelected([]);
      setTollOther("");
      onCreated && onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Live summary for the sticky action bar (route + customer total inc GST).
  // Express and Standard bill identically — Sell Rate is entered manually.
  const effectiveBase = Number(form.base_rate) || 0;
  const sellEx = effectiveBase
    + effectiveBase * (Number(form.fuel_levy_pct) || 0) / 100
    + (Number(form.toll) || 0)
    + ACCESSORIAL_CHARGES.reduce((s, [k, , price]) => s + (form[k] === "Yes" ? price : 0), 0)
    + (form.extra_line_items || []).reduce((s, it) => s + ((it.description || "").trim() ? (Number(it.amount) || 0) : 0), 0);
  const sellInc = sellEx * 1.1;
  const routeText = `${pickup.label || pickup.line1 || "Pick-up"} → ${dropoff.label || dropoff.line1 || "Drop-off"}`;

  return (
    <div className="max-w-5xl" data-testid="create-job-page">
      <h1 className="brand-title text-2xl md:text-3xl mb-1">
        {isClient ? "New Job Request" : "Create New Job"}
      </h1>
      <p className="text-white/60 text-sm mb-6">
        {isClient ? "Your request will be reviewed by admin before activation." : "All fields marked * are required."}
      </p>

      <form onSubmit={submit} className="space-y-5">
        <Section title="Booking" icon={Calendar} accent="#3b82f6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Job Date" required>
              <DatePicker value={form.job_date} onChange={handleJobDate} data-testid="job-date" />
              <p className="text-[11px] text-white/35">
                {form.service_type === "express" ? "Defaults to today for Express" : "Defaults to next day for Standard"}
              </p>
            </Field>
            <Field label="Client Reference / Booking ID" required>
              <input value={form.job_ref} onChange={(e) => set("job_ref", e.target.value)} className="df-input-rect" data-testid="job-ref" placeholder="Enter your reference number (e.g. order or booking #)" required />
            </Field>
            {!isClient && (
              <Field label="Client">
                <select value={form.client_user_id} onChange={(e) => set("client_user_id", e.target.value)} className="df-input-rect" data-testid="customer">
                  <option value="" className="bg-[#15151f]">— Select client —</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id} className="bg-[#15151f]">
                      {c.business_name || c.name} ({c.email})
                    </option>
                  ))}
                </select>
              </Field>
            )}
            {!isClient && (
              <Field label="Assign Driver">
                <select value={form.assigned_driver_id} onChange={(e) => set("assigned_driver_id", e.target.value)} className="df-input-rect" data-testid="assign-driver">
                  <option value="" className="bg-[#15151f]">— Unassigned —</option>
                  {drivers.map((d) => (
                    <option key={d.id} value={d.id} className="bg-[#15151f]">
                      {d.name} ({d.email})
                    </option>
                  ))}
                </select>
              </Field>
            )}
          </div>
        </Section>

        <Section title="Route" icon={Route} accent="#19e6c8" desc="Where the freight is collected and delivered">
          {/* PICK-UP → DROP-OFF — side by side on desktop, stacked on small screens */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-4 lg:gap-3 lg:items-center">
            <AddressFields title="Pick-Up Address" value={pickup} onChange={setPickup} saved={savedAddresses} testid="pickup" accent="#19e6c8" />
            <div className="hidden lg:flex justify-center text-white/45" aria-hidden="true">
              <span className="w-9 h-9 rounded-full bg-white/[0.04] border border-white/12 flex items-center justify-center"><ArrowRight size={16} /></span>
            </div>
            <AddressFields title="Drop-Off Address" value={dropoff} onChange={setDropoff} saved={savedAddresses} testid="dropoff" accent="#a78bfa" />
          </div>

          {/* SHIPMENT TYPE — "Single" is the default & common case; the multi_*
              options are rare and reveal the pickup/delivery stops editor below. */}
          <div className="mt-4">
            <Field label="Shipment Type">
              <select
                value={form.shipment_mode}
                onChange={(e) => set("shipment_mode", e.target.value)}
                className="df-input-rect sm:max-w-sm"
                data-testid="shipment-mode"
              >
                {SHIPMENT_MODES.map((m) => (
                  <option key={m.value} value={m.value} className="bg-[#15151f]">{m.label}</option>
                ))}
              </select>
            </Field>
          </div>

          {/* INTERMEDIATE STOPS / LEGS — only surfaced once the shipment is
              declared multi-pickup/multi-delivery (progressive disclosure keeps
              the common single/single booking exactly as simple as before). */}
          {isMultiStop && (
            <div className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <label className="block font-semibold text-white/85 text-sm">Additional Pickups / Deliveries / Stops</label>
                <button
                  type="button"
                  onClick={() => setLegs((l) => [...l, { address: "", stop_type: defaultStopType(), notes: "", contact_name: "", contact_phone: "" }])}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/10 hover:bg-white/15 text-white"
                >
                  <Plus size={13} /> Add Stop
                </button>
              </div>
              {legs.length === 0 && (
                <p className="text-xs text-white/30 py-2">Add each extra sender or receiver as its own stop — each gets its own signature and proof of delivery.</p>
              )}
              <div className="space-y-3">
                {legs.map((leg, i) => (
                  <div key={i} className="border border-white/10 rounded-xl p-4 bg-white/[0.02] space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-[#19e6c8] uppercase tracking-widest">Stop {i + 1}</span>
                      <button
                        type="button"
                        onClick={() => setLegs((l) => l.filter((_, idx) => idx !== i))}
                        className="ml-auto text-red-400 hover:text-red-300"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="sm:col-span-2">
                        <StopAddressField
                          value={leg.address}
                          onChange={(v) => setLegs((l) => l.map((s, idx) => idx === i ? { ...s, address: v } : s))}
                          onResolved={(p) => setLegs((l) => l.map((s, idx) => idx === i ? { ...s, address: p.formatted || p.line1 } : s))}
                          onPickSaved={(a) => setLegs((l) => l.map((s, idx) => idx === i ? {
                            ...s,
                            contact_name: s.contact_name || a.contact_name || "",
                            contact_phone: s.contact_phone || a.contact_phone || "",
                          } : s))}
                          saved={savedAddresses}
                          placeholder="Stop address * — search saved locations, search Google Maps, or type manually"
                          className="df-input-rect w-full"
                          testid={`stop-address-${i}`}
                          required
                        />
                      </div>
                      <select
                        value={leg.stop_type}
                        onChange={(e) => setLegs((l) => l.map((s, idx) => idx === i ? { ...s, stop_type: e.target.value } : s))}
                        className="df-input-rect"
                      >
                        <option value="pickup" className="bg-[#15151f]">Additional Pickup</option>
                        <option value="intermediate" className="bg-[#15151f]">Intermediate Stop</option>
                        <option value="dropoff" className="bg-[#15151f]">Delivery</option>
                      </select>
                      <input
                        value={leg.contact_name}
                        onChange={(e) => setLegs((l) => l.map((s, idx) => idx === i ? { ...s, contact_name: e.target.value } : s))}
                        placeholder="Contact name"
                        className="df-input-rect"
                      />
                      <input
                        value={leg.contact_phone}
                        onChange={(e) => setLegs((l) => l.map((s, idx) => idx === i ? { ...s, contact_phone: e.target.value } : s))}
                        placeholder="Contact phone"
                        className="df-input-rect"
                      />
                      <input
                        value={leg.notes}
                        onChange={(e) => setLegs((l) => l.map((s, idx) => idx === i ? { ...s, notes: e.target.value } : s))}
                        placeholder="Notes for this stop"
                        className="df-input-rect sm:col-span-2"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>

        <Section title="Service Options" icon={Settings} accent="#06b6d4" desc={!hidePricing ? "Accessorial charges billed to the customer" : "Select any extra handling required"}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {ACCESSORIAL_CHARGES.map(([k, label, price]) => (
              <Field key={k} label={!hidePricing ? `${label} ($${price})` : label} required>
                <select value={form[k]} onChange={(e) => set(k, e.target.value)} className="df-input-rect" data-testid={k.replace(/_/g, "-")}>
                  {YN.map((o) => <option key={o} value={o} className="bg-[#15151f]">{o}</option>)}
                </select>
              </Field>
            ))}
          </div>
        </Section>

        <Section title="Freight & Items" icon={Package} accent="#f59e0b">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Service Type">
              <select value={form.service_type} onChange={(e) => handleServiceType(e.target.value)} className="df-input-rect" data-testid="service-type">
                {SERVICE_TYPES.map((s) => <option key={s.value} value={s.value} className="bg-[#15151f]">{s.label}</option>)}
              </select>
            </Field>
            <Field label="Freight Type">
              <select value={freightSel} onChange={(e) => handleFreightSel(e.target.value)} className="df-input-rect" data-testid="freight-type">
                {[...FREIGHT_TYPES, FREIGHT_OTHER].map((o) => <option key={o} value={o} className="bg-[#15151f]">{o}</option>)}
              </select>
              {freightSel === FREIGHT_OTHER && (
                <input
                  value={form.freight_type}
                  onChange={(e) => set("freight_type", e.target.value)}
                  placeholder="Specify freight type"
                  className="df-input-rect mt-2"
                  data-testid="freight-type-other"
                  required
                />
              )}
            </Field>
            <Field label="Item Type + Qty">
              <ItemMultiSelect rows={itemRows} onRowsChange={setItemRows} />
            </Field>
            <Field label="Volume (cbm)">
              <input type="number" step="0.01" value={form.volume_cbm} onChange={(e) => set("volume_cbm", e.target.value)} className="df-input-rect" data-testid="volume" />
            </Field>
            <Field label="Weight (kgs)">
              <input type="number" value={form.weight_kgs} onChange={(e) => set("weight_kgs", e.target.value)} className="df-input-rect" data-testid="weight" />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <Field label="Dimensions">
              <textarea rows="3" placeholder="120x110x80, 150x120x200..." value={form.dimensions} onChange={(e) => set("dimensions", e.target.value)} className="df-input-rect resize-none" data-testid="dimensions" />
            </Field>
            <Field label="Additional Notes">
              <textarea rows="3" placeholder="CHEPS, time slots, important messages..." value={form.notes} onChange={(e) => set("notes", e.target.value)} className="df-input-rect resize-none" data-testid="notes" />
            </Field>
          </div>
        </Section>

        {!hidePricing && (
          <Section title="Pricing (AUD)" icon={DollarSign} accent="#22c55e">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Driver Rate (cost)">
                <div className="relative">
                  <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                  <input
                    type="number" step="0.01" min="0"
                    value={form.buy_rate}
                    onChange={(e) => set("buy_rate", e.target.value)}
                    className="df-input-rect !pl-8"
                    placeholder="0.00"
                  />
                </div>
              </Field>
              <Field label="Customer Rate">
                <div className="relative">
                  <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                  <input
                    type="number" step="0.01" min="0"
                    value={form.base_rate}
                    onChange={(e) => set("base_rate", e.target.value)}
                    className="df-input-rect !pl-8"
                    placeholder="0.00"
                  />
                </div>
              </Field>
              <Field label="Fuel Levy (% of base)">
                <div className="relative">
                  <input
                    type="number" step="0.1" min="0" max="100"
                    value={form.fuel_levy_pct}
                    onChange={(e) => set("fuel_levy_pct", e.target.value)}
                    className="df-input-rect !pr-8"
                    placeholder="0"
                  />
                  <Percent size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                </div>
              </Field>
              <Field label="Toll">
                <TollMultiSelect
                  selected={tollSelected}
                  otherAmount={tollOther}
                  onChangeSelected={setTollSelected}
                  onChangeOtherAmount={setTollOther}
                />
              </Field>
            </div>

            <div className="mt-4 pt-3 border-t border-white/10">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-white/50 uppercase tracking-widest">Custom Line Items</span>
                <button
                  type="button"
                  onClick={addExtraLineItem}
                  className="text-xs font-semibold px-2 py-1 rounded-lg flex items-center gap-1"
                  style={{ background: "rgba(34,197,94,0.15)", color: "#22c55e" }}
                  data-testid="add-extra-line-item"
                >
                  <Plus size={12} /> Add item
                </button>
              </div>
              {(form.extra_line_items || []).length === 0 ? (
                <p className="text-[11px] text-white/30">
                  E.g. “Shrink wrapping charges - Interport”. Items added here flow straight onto the invoice as their own line.
                </p>
              ) : (
                <div className="space-y-2">
                  {form.extra_line_items.map((item, idx) => (
                    <div key={item.id || idx} className="flex gap-2 items-center">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => updateExtraLineItem(idx, "description", e.target.value)}
                        placeholder="Description (e.g. Shrink wrapping charges - Interport)"
                        className="df-input-rect flex-1"
                        data-testid={`extra-line-desc-${idx}`}
                      />
                      <div className="relative w-28 shrink-0">
                        <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                        <input
                          type="number" step="0.01"
                          value={item.amount}
                          onChange={(e) => updateExtraLineItem(idx, "amount", e.target.value)}
                          className="df-input-rect !pl-8"
                          placeholder="0.00"
                          data-testid={`extra-line-amount-${idx}`}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeExtraLineItem(idx)}
                        className="p-2 rounded-lg text-red-400 hover:bg-red-500/10 shrink-0"
                        title="Remove"
                        data-testid={`extra-line-remove-${idx}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <PricingBreakdown form={form} />
          </Section>
        )}

        <Section title="Documents" icon={FileText} accent="#ec4899" desc="Attach Delivery Orders, Outturn Reports, Booking Confirmations, etc. — added to the job and visible to our team and the driver.">
          <div data-testid="create-job-docs">
            {pendingDocs.length > 0 && (
              <div className="space-y-2 mb-3">
                {pendingDocs.map((pd) => (
                  <div key={pd.id} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white/[0.03] border border-white/10">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white/85 truncate">{pd.file.name}</div>
                      <div className="text-[10px] text-white/40">{pd.doc_type}</div>
                    </div>
                    <button type="button" onClick={() => setPendingDocs((d) => d.filter((x) => x.id !== pd.id))}
                      className="px-2 py-1.5 rounded-lg text-xs shrink-0" style={{ background: "rgba(239,68,68,0.1)", color: "#ef4444" }} title="Remove">
                      <Trash2 size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <select value={docType} onChange={(e) => setDocType(e.target.value)} className="df-input-rect !py-1.5 !text-sm !w-auto" data-testid="create-job-doc-type">
                {DOC_TYPES.map((t) => <option key={t} value={t} className="bg-[#15151f]">{t}</option>)}
              </select>
              <label className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer" style={{ background: "rgba(25,230,200,0.1)", color: "#19e6c8" }}>
                <Upload size={13} /> Add Files
                <input type="file" multiple hidden
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setPendingDocs((d) => [...d, ...files.map((f, i) => ({ id: `${f.name}-${f.size}-${d.length + i}`, file: f, doc_type: docType }))]);
                    e.target.value = "";
                  }}
                  data-testid="create-job-doc-input" />
              </label>
            </div>
          </div>
        </Section>

        {/* Sticky action bar — route + live total, always-visible submit on desktop. */}
        <div className="static lg:sticky lg:bottom-0 z-20 rounded-2xl border border-white/10 bg-[#0a1020]/95 backdrop-blur px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0 text-sm">
            <div className="text-white/80 truncate max-w-[58vw] lg:max-w-lg flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#19e6c8] shrink-0" />
              <span className="truncate">{routeText}</span>
            </div>
            {!hidePricing && (
              <div className="text-[11px] text-white/45 mt-0.5">Customer total (inc GST): <span className="text-emerald-300 font-mono">${sellInc.toFixed(2)}</span></div>
            )}
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="px-7 py-3 rounded-xl text-white font-display tracking-widest text-sm shrink-0"
            style={{ background: "linear-gradient(90deg, #2b5fff, #3b82f6)" }}
            data-testid="create-job-submit"
          >
            {submitting ? "..." : (isClient ? "Submit Request" : "Create Job")}
          </button>
        </div>
      </form>
    </div>
  );
}

// Inline pickup / drop-off entry — mirrors the address-book fields. The Business
// Name field searches saved address-book locations (selecting one fills every
// field); Address Line 1 uses Google Places to auto-fill suburb/state/postcode.
function AddressFields({ title, value, onChange, saved = [], testid, accent = "#19e6c8" }) {
  const upd = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="min-w-0">
      <label className="font-semibold text-sm mb-2 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: accent }} />
        <span className="text-white/85">{title}*</span>
      </label>
      <div className="border border-white/10 rounded-xl p-4 bg-white/[0.02] grid gap-3" style={{ boxShadow: `inset 3px 0 0 ${accent}66` }}>
        <BusinessNameField
          value={value.label}
          options={saved}
          onType={(v) => upd("label", v)}
          onPick={(a) => onChange({
            label: a.label || "", line1: a.line1 || "", line2: a.line2 || "",
            suburb: a.suburb || "", state: a.state || "", postcode: a.postcode || "",
            contact_name: a.contact_name || "", contact_phone: a.contact_phone || "",
            notes: a.notes || "", operating_hours: a.operating_hours || "",
          })}
          testid={`${testid}-label`}
        />
        <AddressAutocomplete
          className="df-input-rect"
          placeholder="Address Line 1 — start typing to search"
          value={value.line1}
          onChange={(v) => upd("line1", v)}
          onResolved={(p) => onChange({ ...value, line1: p.line1, suburb: p.suburb, state: p.state, postcode: p.postcode })}
          data-testid={`${testid}-line1`}
          required
        />
        <input className="df-input-rect" placeholder="Address Line 2 (unit, suite, floor — optional)" value={value.line2} onChange={(e) => upd("line2", e.target.value)} data-testid={`${testid}-line2`} />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input className="df-input-rect" placeholder="Suburb" value={value.suburb} onChange={(e) => upd("suburb", e.target.value)} data-testid={`${testid}-suburb`} />
          <input className="df-input-rect" placeholder="State" value={value.state} onChange={(e) => upd("state", e.target.value)} data-testid={`${testid}-state`} />
          <input className="df-input-rect" placeholder="Postcode" value={value.postcode} onChange={(e) => upd("postcode", e.target.value)} data-testid={`${testid}-postcode`} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input className="df-input-rect" placeholder="Contact name" value={value.contact_name} onChange={(e) => upd("contact_name", e.target.value)} />
          <input className="df-input-rect" placeholder="Contact phone" value={value.contact_phone} onChange={(e) => upd("contact_phone", e.target.value)} />
        </div>
        <input className="df-input-rect" placeholder="Notes (optional)" value={value.notes} onChange={(e) => upd("notes", e.target.value)} />
        <input
          className="df-input-rect"
          placeholder="Operating hours (optional) — e.g. Mon–Fri 7am–4pm"
          value={value.operating_hours}
          onChange={(e) => upd("operating_hours", e.target.value)}
        />
      </div>
    </div>
  );
}

// Business Name input with a dropdown of matching saved address-book entries.
// Typing filters by business name / address; picking one fills all fields.
function BusinessNameField({ value, options, onType, onPick, testid }) {
  const [open, setOpen] = useState(false);
  const q = (value || "").trim().toLowerCase();
  const matches = (q
    ? options.filter((a) =>
        (a.label || "").toLowerCase().includes(q) ||
        (a.line1 || "").toLowerCase().includes(q) ||
        (a.suburb || "").toLowerCase().includes(q))
    : options
  ).slice(0, 8);

  return (
    <div className="relative">
      <input
        className="df-input-rect"
        placeholder="Business name — search saved locations or type a new one"
        value={value}
        onChange={(e) => { onType(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        data-testid={testid}
        autoComplete="off"
      />
      {open && matches.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-[#15151f] border border-white/12 rounded-xl shadow-xl max-h-64 overflow-auto">
          {matches.map((a) => (
            <button
              key={a.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onPick(a); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-white/5 border-b border-white/5 last:border-0"
            >
              <div className="text-sm text-white truncate">{a.label || a.line1}</div>
              <div className="text-xs text-white/50 truncate">
                {[a.line1, a.line2, a.suburb, a.state, a.postcode].filter(Boolean).join(", ")}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PricingBreakdown({ form }) {
  const buyRate  = parseFloat(form.buy_rate)      || 0;
  // Express and Standard bill identically — Sell Rate is entered manually.
  const sellRate = parseFloat(form.base_rate) || 0;
  const fuelPct  = parseFloat(form.fuel_levy_pct) || 0;
  const toll     = parseFloat(form.toll)          || 0;

  // Accessorial charges, fuel levy and toll apply to the customer (sell) side
  // only. Driver Rate is fully manual — no fuel levy or other extras are ever
  // added to it; the only thing applied on top is GST, matching _job_buy() in
  // backend/server.py.
  const activeAcc = ACCESSORIAL_CHARGES.filter(([k]) => form[k] === "Yes");
  const accTotal  = activeAcc.reduce((sum, [, , price]) => sum + price, 0);

  // Custom line items apply to the customer (sell) side only, same as
  // accessorials — matching how _job_line_items() bills them server-side.
  const extraItems = (form.extra_line_items || []).filter((it) => (it.description || "").trim());
  const extraTotal = extraItems.reduce((sum, it) => sum + (parseFloat(it.amount) || 0), 0);

  if (buyRate === 0 && sellRate === 0 && accTotal === 0 && extraTotal === 0) return null;

  const fmt = (n) =>
    `$${Math.abs(n).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const buy = { total: +(buyRate * 1.1).toFixed(2), gst: +(buyRate * 0.1).toFixed(2) };

  const fuel     = +(sellRate * fuelPct / 100).toFixed(2);
  const subtotal = +(sellRate + fuel + toll + accTotal + extraTotal).toFixed(2);
  const sellGst  = +(subtotal * 0.1).toFixed(2);
  const sell     = { subtotal, gst: sellGst, total: +(subtotal + sellGst).toFixed(2) };

  const margin = +(sell.total - buy.total).toFixed(2);

  const Row = ({ label, buyVal, sellVal, highlight }) => (
    <div className={`grid grid-cols-3 gap-2 text-xs py-1 ${highlight ? "border-t border-white/10 pt-2" : ""}`}>
      <span className="text-white/50">{label}</span>
      <span className={`text-right font-semibold ${highlight ? "text-[#8a5cff]" : "text-white"}`}>{buyVal}</span>
      <span className={`text-right font-semibold ${highlight ? "text-[#19e6c8]" : "text-white"}`}>{sellVal}</span>
    </div>
  );

  return (
    <div className="pt-3 border-t border-white/10 space-y-1">
      <div className="grid grid-cols-3 gap-2 text-xs pb-1 border-b border-white/10">
        <span className="text-white/30 uppercase tracking-wider">Line item</span>
        <span className="text-right text-[#8a5cff]/70 uppercase tracking-wider">Driver Rate</span>
        <span className="text-right text-[#19e6c8]/70 uppercase tracking-wider">Customer Rate</span>
      </div>
      <Row label="Base Rate" buyVal={fmt(buyRate)} sellVal={fmt(sellRate)} />
      <Row label={`Fuel Levy (${fuelPct}%)`} buyVal="—" sellVal={fmt(fuel)} />
      <Row label="Toll"                   buyVal="—"         sellVal={fmt(toll)} />
      {activeAcc.map(([k, label, price]) => (
        <Row key={k} label={label} buyVal="—" sellVal={fmt(price)} />
      ))}
      {extraItems.map((it, i) => (
        <Row key={it.id || i} label={it.description} buyVal="—" sellVal={fmt(parseFloat(it.amount) || 0)} />
      ))}
      <Row label="Subtotal"               buyVal={fmt(buyRate)} sellVal={fmt(sell.subtotal)} highlight />
      <Row label="GST (10%)"              buyVal={fmt(buy.gst)}      sellVal={fmt(sell.gst)} />
      <div className="grid grid-cols-3 gap-2 rounded-lg px-3 py-2.5 mt-1"
           style={{ background: "linear-gradient(90deg, #1a1530, #0f0f1a)" }}>
        <span className="text-xs text-white/60 font-semibold tracking-widest uppercase">Total (incl. GST)</span>
        <span className="text-right text-[#8a5cff] font-bold">{fmt(buy.total)}</span>
        <span className="text-right text-[#19e6c8] font-bold">{fmt(sell.total)}</span>
      </div>
      {(buyRate > 0 && sellRate > 0) && (
        <div className={`text-xs px-1 pt-1 ${margin >= 0 ? "text-green-400" : "text-red-400"}`}>
          Margin: {margin < 0 ? "-" : ""}{fmt(Math.abs(margin))}
          {sell.total > 0 ? ` (${((margin / sell.total) * 100).toFixed(1)}%)` : ""}
        </div>
      )}
    </div>
  );
}
